<template>
  <div class="m19-overuse-modal m19-enterprise">
    <q-linear-progress
      v-if="loading"
      indeterminate
      color="primary"
      class="m19-enterprise__progress"
    />

    <template v-else-if="!loading">
      <div class="m19-enterprise__toolbar">
        <div class="m19-enterprise__toolbar-controls">
          <q-select
            v-model="selectedSbsSkladId"
            :options="skladOptions"
            option-value="sbs_sklad_id"
            option-label="sklad_name"
            emit-value
            map-options
            label="Склад учёта 1С"
            dense
            outlined
            clearable
            hide-bottom-space
            class="m19-enterprise__sklad"
            popup-content-class="m19-enterprise__sklad-menu"
            :loading="skladOptionsLoading"
          />
          <q-input
            v-model="searchQuery"
            dense
            outlined
            :placeholder="isSkladSelected ? 'Поиск…' : 'Сначала выберите склад'"
            clearable
            hide-bottom-space
            class="m19-enterprise__search"
            :disable="!isSkladSelected"
          >
            <template #prepend>
              <q-icon name="search" size="16px" color="grey-7" class="q-pr-xs" aria-hidden="true" />
            </template>
          </q-input>
        </div>
        <div v-if="loading1cBalances || wbsSkladReloading" class="m19-enterprise__hint">
          <q-spinner-dots color="primary" size="xs" />
          <span>{{ wbsSkladReloading ? "Обновление материалов…" : "Остатки 1С…" }}</span>
        </div>
      </div>
      <div v-if="!isSkladSelected" class="m19-enterprise__sklad-prompt" role="status">
        <q-icon
          name="error_outline"
          color="primary"
          size="20px"
          class="m19-enterprise__sklad-prompt-icon"
          aria-hidden="true"
        />
        <span class="m19-enterprise__sklad-prompt-text">Выберите склад учёта 1С</span>
      </div>
    </template>

    <div
      v-if="flatMaterialGroups.length > 0 && isSkladSelected"
      class="m19-enterprise__table-host"
      role="region"
      aria-label="Таблица материалов М19"
    >
      <div class="m19-enterprise__table-scroll">
        <table class="m19-grid">
          <thead class="m19-grid__head">
            <tr>
              <th class="m19-grid__th m19-grid__th--chk" scope="col">
                <q-checkbox
                  :model-value="isFlatAllSelected"
                  :indeterminate="isFlatSomeSelected"
                  dense
                  size="sm"
                  color="primary"
                  @update:model-value="toggleFlatSelectAll"
                />
              </th>
              <th class="m19-grid__th m19-grid__th--mat" scope="col">
                <div class="m19-th-mat-stack">
                  <span class="m19-th-mat-stack__top">Материал</span>
                  <span class="m19-th-mat-stack__bot">Ед. изм.</span>
                </div>
              </th>
              <th class="m19-grid__th m19-grid__th--smeta text-center" scope="col">
                <div class="m19-th-smeta-stack" title="Кол-во по смете и кол-во оперативного факта">
                  <span class="m19-th-smeta-stack__top">Кол-во по смете</span>
                  <span class="m19-th-smeta-stack__bot">Кол-во опер-факт</span>
                </div>
              </th>
              <th class="m19-grid__th m19-grid__th--num text-right" scope="col">
                Списано
              </th>
              <th class="m19-grid__th m19-grid__th--num text-right" scope="col">
                Остаток
              </th>
              <th
                class="m19-grid__th m19-grid__th--num m19-grid__th--reserve text-right"
                scope="col"
                title="Количество по материалу в уже созданных М‑19, по которым файл ещё не отмечен как отправленный в BI (is_file_sent_bi). После успешной выгрузки файла резерв по документу в этой сумме обнуляется. Учитывается со свободным остатком при проверке «Кол‑во к списанию» и «Кол‑во в бух» на выбранном складе."
              >
                Резерв
              </th>
              <th class="m19-grid__th m19-grid__th--qty text-right" scope="col">
                Кол-во к списанию
              </th>
              <th class="m19-grid__th m19-grid__th--cmt" scope="col">Комментарий</th>
              <th class="m19-grid__th m19-grid__th--sklad text-center" scope="col">
                <div class="m19-th-sklad-stack">
                  <span class="m19-th-sklad-stack__top">Склады</span>
                  <span class="m19-th-sklad-stack__bot">Номенклатура</span>
                </div>
              </th>
              <th class="m19-grid__th m19-grid__th--buh text-right" scope="col">
                Кол-во в бух
              </th>
            </tr>
          </thead>
          <tbody>
            <template v-for="mg in flatMaterialGroups" :key="mg.wbsKey">
              <tr
                v-for="(trow, tidx) in getMaterialTableRows(mg)"
                :key="
                  trow.kind === 'draft'
                    ? trow.mat._key
                    : `exhausted-${mg.wbsKey}`
                "
                :class="['m19-grid__row', tableRowClasses(mg, trow, tidx)]"
              >
                <td class="m19-grid__td m19-grid__td--chk">
                  <q-checkbox
                    v-if="trow.kind === 'draft'"
                    v-model="trow.mat.selected"
                    dense
                    size="sm"
                    color="primary"
                    @update:model-value="onMaterialSelect(trow.mat)"
                  />
                  <q-checkbox
                    v-else-if="trow.kind === 'exhausted'"
                    :model-value="false"
                    disable
                    dense
                    size="sm"
                    color="grey"
                  />
                </td>
                <td
                  v-if="tidx === 0"
                  class="m19-grid__td m19-grid__td--mat"
                  :rowspan="materialTableRowSpan(mg)"
                >
                  <div class="m19-mat">
                    <div class="m19-mat__touch">
                      <div class="m19-mat__title">
                        {{ baseMaterialRow(mg).material_name }}
                        <span class="m19-mat__unit-inline">
                          ({{ materialUnitDisplay(baseMaterialRow(mg)) }})
                        </span>
                      </div>
                    </div>
                  </div>
                </td>
                <td
                  v-if="tidx === 0"
                  class="m19-grid__td m19-grid__td--smeta text-center"
                  :rowspan="materialTableRowSpan(mg)"
                >
                  <div class="m19-so-stack">
                    <div class="m19-so-stack__line m19-so-stack__line--value-only">
                      <span class="m19-so-stack__val m19-so-stack__val--smeta">{{
                        formatQtyNum(baseMaterialRow(mg).material_amount)
                      }}</span>
                    </div>
                    <div class="m19-so-stack__line m19-so-stack__line--value-only">
                      <span class="m19-so-stack__val m19-so-stack__val--oper">{{
                        formatQty(baseMaterialRow(mg).oper_fact_qty)
                      }}</span>
                    </div>
                  </div>
                </td>
                <td
                  v-if="tidx === 0"
                  class="m19-grid__td m19-grid__td--num text-right m19-num"
                  :rowspan="materialTableRowSpan(mg)"
                >
                  {{ formatQty(baseMaterialRow(mg).m19_qty_sbs) }}
                </td>
                <td
                  v-if="tidx === 0"
                  class="m19-grid__td m19-grid__td--num text-right m19-num tabular-nums"
                  :rowspan="materialTableRowSpan(mg)"
                  :class="remainderToneClass(baseMaterialRow(mg))"
                >
                  {{ formatQtyNum(remainderDisplay(baseMaterialRow(mg))) }}
                </td>
                <td
                  v-if="tidx === 0"
                  class="m19-grid__td m19-grid__td--num m19-grid__td--reserve text-right m19-num tabular-nums"
                  :rowspan="materialTableRowSpan(mg)"
                >
                  {{ formatReserveCell(baseMaterialRow(mg)) }}
                </td>
                <td class="m19-grid__td m19-grid__td--qty text-right">
                  <span v-if="trow.kind === 'exhausted'" class="text-grey-5">—</span>
                  <div v-else class="m19-inline-qty">
                    <q-input
                      :model-value="trow.mat.fact_qty"
                      type="number"
                      dense
                      outlined
                      :min="0"
                      hide-bottom-space
                      class="m19-input-qty"
                      :class="{
                        'm19-input-qty--warn':
                          trow.mat.selected &&
                          (exceedsSmetaLimit(trow.mat) || exceedsBuhAggregateForMat(trow.mat)),
                      }"
                      input-class="text-right tabular-nums"
                      :disable="!trow.mat.selected"
                      @update:model-value="(v) => onFactQtyInput(trow.mat, v)"
                    />
                    <q-icon
                      v-if="trow.mat.selected && exceedsSmetaLimit(trow.mat)"
                      name="warning_amber"
                      color="amber-8"
                      size="18px"
                      class="m19-inline-qty__warn cursor-help"
                      aria-label="Превышение лимита сметы"
                    >
                      <q-tooltip max-width="280px" anchor="top middle" self="bottom middle">
                        Сумма учтённого по М19 и количества к списанию превышает смету. Уменьшите значение
                        или проверьте данные.
                      </q-tooltip>
                    </q-icon>
                    <q-icon
                      v-if="trow.mat.selected && exceedsBuhAggregateForMat(trow.mat)"
                      name="inventory_2"
                      color="deep-orange-6"
                      size="18px"
                      class="m19-inline-qty__warn cursor-help q-ml-xs"
                      aria-label="Превышение количества в бухучёте"
                    >
                      <q-tooltip max-width="300px" anchor="top middle" self="bottom middle">
                        Введено больше, чем свободный остаток по 1С на этом складе (колонка «Кол-во в бух» минус «Резерв» по М‑19 без отправки файла в BI). Сохранение будет недоступно, пока не уменьшите числа или не смените склад.
                      </q-tooltip>
                    </q-icon>
                  </div>
                </td>
                <td class="m19-grid__td m19-grid__td--cmt">
                  <span v-if="trow.kind === 'exhausted'" class="text-grey-5">—</span>
                  <q-input
                    v-else
                    v-model="trow.mat.comments"
                    type="textarea"
                    autogrow
                    dense
                    outlined
                    placeholder="Комментарий"
                    hide-bottom-space
                    class="m19-input-comment"
                    :disable="!trow.mat.selected"
                    :input-style="{ minHeight: '36px', maxHeight: '72px', lineHeight: '1.35' }"
                  />
                </td>
                <td
                  v-if="tidx === 0"
                  class="m19-grid__td m19-grid__td--sklad"
                  :rowspan="materialTableRowSpan(mg)"
                >
                  <div v-if="selectedSkladDisplayText" class="m19-sklad-cell">
                    <div class="m19-sklad-cell__touch">
                      <div class="m19-sklad-cell__title" :title="selectedSkladDisplayText">
                        {{ selectedSkladDisplayText }}
                      </div>
                      <div
                        v-if="materialName1cDisplay(baseMaterialRow(mg).material_id)"
                        class="m19-sklad-cell__sub"
                        :title="materialName1cDisplay(baseMaterialRow(mg).material_id)"
                      >
                        {{ materialName1cDisplay(baseMaterialRow(mg).material_id) }}
                      </div>
                    </div>
                    <q-tooltip
                      anchor="top left"
                      self="bottom left"
                      class="m19-sklad-tooltip"
                      :offset="[0, 8]"
                    >
                      <div class="text-body2">{{ selectedSkladDisplayText }}</div>
                      <div
                        v-if="materialName1cDisplay(baseMaterialRow(mg).material_id)"
                        class="text-caption text-grey-4 q-mt-xs"
                      >
                        {{ materialName1cDisplay(baseMaterialRow(mg).material_id) }}
                      </div>
                    </q-tooltip>
                  </div>
                  <span v-else class="text-grey-5">—</span>
                </td>
                <td
                  v-if="tidx === 0"
                  class="m19-grid__td m19-grid__td--buh text-right"
                  :rowspan="materialTableRowSpan(mg)"
                >
                  <span
                    class="tabular-nums m19-buh-value"
                    :class="buhQtyCellClass(baseMaterialRow(mg).material_id)"
                  >
                    {{ qty1cDisplay(baseMaterialRow(mg).material_id) }}
                  </span>
                </td>
              </tr>
            </template>
          </tbody>
        </table>
      </div>
    </div>

    <NotFoundText v-if="!loading && isSkladSelected && flatMaterialGroups.length === 0" />

    <div v-if="hasMaterials && !loading" class="m19-enterprise__footer">
      <CustomButton
        color="white"
        text-color="grey-8"
        outlined
        :disabled="saving"
        class="m19-enterprise__btn"
        @click="closeModal()"
      >
        Отмена
      </CustomButton>
      <CustomButton
        color="primary"
        :loading="saving"
        :disabled="saving || !isSkladSelected"
        class="m19-enterprise__btn m19-enterprise__btn--primary"
        @click="onSave"
      >
        Создать
      </CustomButton>
    </div>
  </div>
</template>

<script setup>
import {
  ref,
  computed,
  watch,
  onMounted,
  onBeforeUnmount,
} from "vue";
import { useAppStore } from "@/services/store/app";
import { useCustomSnackbar } from "@/_helpers/hooks/useCustomSnackbar";
import CustomButton from "@/components/common/CustomButton.vue";
import NotFoundText from "@/components/common/NotFoundText";
import {
  getM19OverWbs,
  createM19Over,
  downloadM19OverPreview,
  getM19OverSkladOptions,
  previewM19OverWarehouseBalances,
} from "@/views/ResidentialSettings/services";

const props = defineProps({
  data: {
    type: Object,
    default: () => ({}),
  },
});

const { closeModal } = useAppStore();
const { showSucceedSnackbar, showErrorSnackbar } = useCustomSnackbar();

const loading = ref(false);
const saving = ref(false);
const downloadingPreview = ref(false);
const wbsData = ref([]);
const searchQuery = ref("");
const searchQueryDebounced = ref("");
/** Поиск по таблице: debounce, чтобы не дёргать фильтрацию при быстром вводе */
let searchDebounceTimer = null;

const skladOptions = ref([]);
const skladOptionsLoading = ref(false);
const selectedSbsSkladId = ref(null);
const loading1cBalances = ref(false);
const wbsSkladReloading = ref(false);
const qty1cByMaterialId = ref({});
const materialName1cByMaterialId = ref({});

/**
 * Номенклатуры, для которых остаток из 1С (SBS) в другой кратности, чем ввод в Мастере (тираж «1000 шт» / тоннаж и т.п.);
 * сверка как в material_overuse__check_warehouse (наше количество / 1000 к остатку 1С).
 */
const MATERIAL_IDS_1C_BALANCE_IN_TONS = new Set([1000378, 1000009, 1000651, 1000583]);

function isMaterial1cBalanceInTons(materialId) {
  return MATERIAL_IDS_1C_BALANCE_IN_TONS.has(Number(materialId));
}

/** Склад обязателен: таблица и остатки 1С только после выбора. */
const isSkladSelected = computed(() => {
  const id = selectedSbsSkladId.value;
  return id != null && id !== "";
});

let balancesRefreshTimer = null;

watch(searchQuery, (q) => {
  clearTimeout(searchDebounceTimer);
  searchDebounceTimer = setTimeout(() => {
    searchQueryDebounced.value = typeof q === "string" ? q : "";
  }, 280);
});

function scheduleRefreshBalances() {
  clearTimeout(balancesRefreshTimer);
  balancesRefreshTimer = setTimeout(() => {
    refreshWarehouseBalances();
  }, 450);
}

function wbsGroupKey(m) {
  return String(m.material_id);
}

function baseMaterialRow(mg) {
  return mg.lines[0];
}

/** Ед. изм. в конце названия материала (колонка «Материал»). */
function materialUnitDisplay(row) {
  if (row == null) return "—";
  const u = row.material_unit_name;
  if (u == null || String(u).trim() === "") return "—";
  return String(u).trim();
}

/** Название выбранного в модалке склада для колонки «Склады». */
const selectedSkladDisplayText = computed(() => {
  const id = selectedSbsSkladId.value;
  if (id == null || id === "") return null;
  const opt = skladOptions.value.find((o) => o.sbs_sklad_id === id);
  const name = opt?.sklad_name != null ? String(opt.sklad_name).trim() : "";
  if (name) return name;
  return `Склад №${id}`;
});

/** Подпись номенклатуры из справочника 1С (SBS); из ответа material_overuse__warehouse_balances_preview. */
function materialName1cDisplay(materialId) {
  if (selectedSbsSkladId.value == null || selectedSbsSkladId.value === "") {
    return "";
  }
  if (loading1cBalances.value) {
    return "";
  }
  const map = materialName1cByMaterialId.value;
  if (!map || typeof map !== "object") return "";
  if (!Object.prototype.hasOwnProperty.call(map, materialId)) return "";
  const s = map[materialId];
  if (s == null || s === "") return "";
  return String(s).trim();
}

/** Тон строки «Остаток»: зелёный > 0, красный < 0. */
function remainderToneClass(row) {
  const v = remainderDisplay(row);
  if (v == null || Number.isNaN(Number(v))) return "m19-num-tone--muted";
  const n = roundStable(Number(v));
  if (n < -1e-9) return "m19-num-tone--neg";
  if (n > 1e-9) return "m19-num-tone--pos";
  return "m19-num-tone--zero";
}

/** Визуальная модификация «Кол-во в Бух» в ячейке. */
function buhQtyCellClass(materialId) {
  const s = qty1cDisplay(materialId);
  if (s === "…") return "m19-buh-value--muted";
  if (s === "—" || s === "") return "m19-buh-value--muted";
  if (s === "нет в ответе") return "m19-buh-value--warn";
  return "";
}

function qty1cDisplay(materialId) {
  if (selectedSbsSkladId.value == null || selectedSbsSkladId.value === "") {
    return "—";
  }
  if (loading1cBalances.value) {
    return "…";
  }
  const map = qty1cByMaterialId.value;
  if (!map || typeof map !== "object") {
    return "—";
  }
  if (!Object.prototype.hasOwnProperty.call(map, materialId)) {
    return "—";
  }
  const v = map[materialId];
  if (v === null) {
    return "0";
  }
  if (v === undefined) {
    return "—";
  }
  const n = qty1cNumeric(materialId);
  if (n === null || Number.isNaN(n)) {
    return "—";
  }
  return formatQtyNum(n);
}

/** Числовое «Кол-во в бух» в тех же единицах, что резерв и «к списанию» (для «тонных» — ×1000 от ответа 1С). */
function qty1cNumeric(materialId) {
  if (selectedSbsSkladId.value == null || selectedSbsSkladId.value === "") {
    return null;
  }
  if (loading1cBalances.value) {
    return null;
  }
  const map = qty1cByMaterialId.value;
  if (!map || typeof map !== "object") {
    return null;
  }
  if (!Object.prototype.hasOwnProperty.call(map, materialId)) {
    return null;
  }
  const v = map[materialId];
  if (v === undefined) {
    return null;
  }
  if (v === null) {
    return 0;
  }
  const n = Number(v);
  if (Number.isNaN(n)) {
    return null;
  }
  if (isMaterial1cBalanceInTons(materialId)) {
    return roundStable(n * 1000);
  }
  return n;
}

/** По материалу: сумма quantity по строкам материала в сохранённых М‑19, по которым is_file_sent_bi ещё false (файл не отправлен в BI). Столбец «Резерв». */
function pendingReserveQtyUntilFileSentBi(materialId) {
  for (const r of wbsData.value) {
    if (r.material_id === materialId) {
      const v = r.m19_reserve_pending_avr_qty;
      const n = v != null ? Number(v) : 0;
      if (Number.isNaN(n)) return 0;
      return roundStable(Math.max(0, n));
    }
  }
  return 0;
}

/** Доступно к новому списанию при сверке с 1С: «Кол-во в бух» минус «Резерв». */
function qty1cNetAvailableForWrites(materialId) {
  const gross = qty1cNumeric(materialId);
  if (gross == null) return null;
  return roundStable(Math.max(0, gross - pendingReserveQtyUntilFileSentBi(materialId)));
}

/**
 * Выбранные строки того же материала: сумма полей «к списанию» без текущей строки (доля лимита «в бух»).
 */
function sumOtherSelectedFactSameMat(mat) {
  if (mat == null) return 0;
  const mid = mat.material_id;
  const selfKey = mat._key;
  let s = 0;
  for (const r of wbsData.value) {
    if (!r.selected || r.material_id !== mid) continue;
    if (selfKey != null && r._key === selfKey) continue;
    s += Number(r.fact_qty) || 0;
  }
  return roundStable(s);
}

/** Максимально допустимое «к списанию» в строке при выбранном складе и остальных выбранных строках этого материала. */
function maxFactQtyAllowedForMaterialRow(mat) {
  const buhNet = qty1cNetAvailableForWrites(mat?.material_id);
  if (buhNet == null) return null;
  const other = sumOtherSelectedFactSameMat(mat);
  return Math.max(0, roundStable(buhNet - other));
}

/** Ввод: только проверка на неотрицательность; верхнюю границу «в бух» не режем здесь — иначе ощущение «отката». Предупреждение по строкам + блокировка сохранения. */
function onFactQtyInput(mat, raw) {
  if (mat == null || !mat.selected) return;
  if (raw === "" || raw == null) {
    mat.fact_qty = "";
    return;
  }
  const n = Number(raw);
  if (Number.isNaN(n)) {
    mat.fact_qty = raw;
    return;
  }
  mat.fact_qty = roundStable(Math.max(0, n));
}

/** Сумма «к списанию» по всем выбранным строкам одного материала (новый документ). */
function sumSelectedFactForMaterial(materialId) {
  let s = 0;
  for (const r of wbsData.value) {
    if (!r.selected || r.material_id !== materialId) continue;
    s += Number(r.fact_qty) || 0;
  }
  return roundStable(s);
}

function exceedsBuhAggregateForMat(mat) {
  if (mat == null || !mat.selected) return false;
  if (!isSkladSelected.value || loading1cBalances.value) return false;
  const cap = qty1cNetAvailableForWrites(mat.material_id);
  if (cap == null) return false;
  return sumSelectedFactForMaterial(mat.material_id) > cap + 1e-9;
}

/** При выборе строки подставить остаток 1С в поле списания. */
function applyBuhQtyToFact(mat) {
  if (mat == null || !mat.selected) {
    return;
  }
  const cap = maxFactQtyAllowedForMaterialRow(mat);
  if (cap == null) {
    if (mat.fact_qty == null || mat.fact_qty === "") {
      mat.fact_qty = 0;
    }
    return;
  }
  mat.fact_qty = roundStable(cap);
}

async function loadSkladOptions() {
  const projectId = props.data?.project_id;
  skladOptions.value = [];
  if (projectId == null || projectId === "") {
    return;
  }
  skladOptionsLoading.value = true;
  try {
    const envelope = await getM19OverSkladOptions(projectId);
    const rows = Array.isArray(envelope?.data) ? envelope.data : [];
    skladOptions.value = rows;
    selectedSbsSkladId.value = null;
    qty1cByMaterialId.value = {};
    materialName1cByMaterialId.value = {};
  } finally {
    skladOptionsLoading.value = false;
  }
}

async function refreshWarehouseBalances() {
  const projectId = props.data?.project_id;
  const ids = [
    ...new Set(
      filteredWbsRows.value.map((r) => r.material_id).filter((x) => x != null),
    ),
  ].sort((a, b) => a - b);

  if (
    projectId == null ||
    selectedSbsSkladId.value == null ||
    selectedSbsSkladId.value === "" ||
    ids.length === 0
  ) {
    qty1cByMaterialId.value = {};
    materialName1cByMaterialId.value = {};
    loading1cBalances.value = false;
    return;
  }

  loading1cBalances.value = true;
  try {
    const envelope = await previewM19OverWarehouseBalances(
      projectId,
      ids,
      selectedSbsSkladId.value,
    );
    if (envelope == null) {
      qty1cByMaterialId.value = {};
      materialName1cByMaterialId.value = {};
      showErrorSnackbar("Не удалось получить остатки 1С");
      return;
    }
    const payload = envelope?.data;
    if (!payload?.ok) {
      qty1cByMaterialId.value = {};
      materialName1cByMaterialId.value = {};
      const err = payload?.error ?? envelope?.error ?? "Не удалось получить остатки 1С";
      if (String(err).trim()) {
        showErrorSnackbar(String(err));
      }
      return;
    }

    const map = {};
    const nameMap = {};
    for (const id of ids) {
      map[id] = undefined;
      nameMap[id] = "";
    }
    for (const row of payload.balances || []) {
      const mid = row.material_id;
      if (mid == null) continue;
      map[mid] =
        row.remaining != null && row.remaining !== ""
          ? Number(row.remaining)
          : null;
      const n1 = row.material_name_1c;
      if (n1 != null && String(n1).trim() !== "") {
        nameMap[mid] = String(n1).trim();
      }
    }
    const missing = payload.missing_material_ids || [];
    for (const mid of missing) {
      map[mid] = null;
    }
    qty1cByMaterialId.value = map;
    materialName1cByMaterialId.value = nameMap;
  } finally {
    loading1cBalances.value = false;
  }
}

/** Остаток по смете: смета − списано по М19 (СБС); с бэка приходит `remainder_qty`. */
function remainderDisplay(row) {
  if (row == null) return null;
  if (row.remainder_qty != null && row.remainder_qty !== "") {
    return Number(row.remainder_qty);
  }
  const smeta = Number(row.material_amount) || 0;
  const saved = Number(row.total_m19_fact_qty) || 0;
  return smeta - saved;
}

function remainderIsNegative(row) {
  const v = remainderDisplay(row);
  if (v == null || Number.isNaN(Number(v))) return false;
  return roundStable(Number(v)) < -1e-9;
}

/**
 * Остаток по смете исчерпан учётом уже внесённого факта М19 — новое списание недоступно.
 */
function isM19RemainderExhausted(item) {
  const r = remainderDisplay(item);
  if (r != null && !Number.isNaN(Number(r))) {
    return roundStable(Number(r)) <= 1e-9;
  }
  const smeta = roundStable(Number(item.material_amount) || 0);
  const saved = roundStable(Number(item.total_m19_fact_qty) || 0);
  return saved >= smeta - 1e-9;
}

/**
 * Одна видимая строка ввода на материал: только черновик нового М19 (`isDraftRow`),
 * без отдельной строки «сводки» по уже созданным документам (она давала дубль полей).
 */
function getMaterialTableRows(mg) {
  const base = mg.lines[0];
  if (base.m19_remainder_exhausted) {
    return [{ kind: "exhausted", mat: base }];
  }
  const drafts = mg.lines.filter((l) => !l.floor_map_document_id);
  const newDocRows = drafts.filter((l) => l.isDraftRow);
  const rowsToShow = newDocRows.length > 0 ? newDocRows : drafts;
  if (rowsToShow.length > 0) {
    return rowsToShow.map((mat) => ({ kind: "draft", mat }));
  }
  if (mg.lines.length > 0) {
    return [{ kind: "draft", mat: mg.lines[0] }];
  }
  return [];
}

function materialTableRowSpan(mg) {
  return getMaterialTableRows(mg).length;
}

function tableRowClasses(mg, trow, tidx) {
  if (trow.kind === "exhausted") {
    return { "m19-row--exhausted": true };
  }
  const mat = trow.mat;
  return {
    "row-unselected": !mat.selected,
    "row-selected": mat.selected,
    "m19-row--draft": mat.isDraftRow,
    "m19-row--sub": tidx > 0,
    "m19-row--over-smeta": exceedsSmetaLimit(mat) && mat.selected,
    "m19-row--over-buh":
      exceedsBuhAggregateForMat(mat) &&
      mat.selected &&
      isSkladSelected.value &&
      !loading1cBalances.value,
  };
}

/** Группы строк по одному материалу (несколько М19 + черновик) — для rowspan в таблице. */
function groupMaterialsByWbsKey(materials) {
  const map = new Map();
  for (const m of materials) {
    const k = wbsGroupKey(m);
    if (!map.has(k)) map.set(k, []);
    map.get(k).push(m);
  }
  return Array.from(map.values()).map((lines) => {
    const mg = {
      wbsKey: wbsGroupKey(lines[0]),
      lines,
      rowSpan: 0,
    };
    mg.rowSpan = getMaterialTableRows(mg).length;
    return mg;
  });
}

const filteredWbsRows = computed(() => {
  const q = (searchQueryDebounced.value || "").trim().toLowerCase();
  if (!q) return wbsData.value;
  return wbsData.value.filter((r) => r.material_name?.toLowerCase().includes(q));
});

const flatMaterialGroups = computed(() => groupMaterialsByWbsKey(filteredWbsRows.value));

/**
 * После смены склада остатки «в бух» другие — сбрасываем только черновик нового М19,
 * сохранённые по floor_map_document_id строки не трогаем.
 */
function resetDraftMaterialChoicesBecauseSkladChanged() {
  for (const row of wbsData.value) {
    if (row.floor_map_document_id != null && row.floor_map_document_id !== "") {
      continue;
    }
    row.selected = false;
    row.fact_qty = 0;
    row.comments = "";
  }
}

async function reloadWbsAfterSkladSelection() {
  const residentId = props.data?.params?.resident_id;
  const projectTypeId =
    props.data?.params?.project_type_id ?? props.data?.filters?.project_type_id ?? null;
  if (residentId == null || projectTypeId == null || projectTypeId === "") {
    return;
  }
  wbsSkladReloading.value = true;
  try {
    const sk = selectedSbsSkladId.value;
    const res = await getM19OverWbs(
      residentId,
      Number(projectTypeId),
      sk != null && sk !== "" ? Number(sk) : undefined,
    );
    if (!res) return;
    wbsData.value = normalizeWbsRows(res.data || []);
  } finally {
    wbsSkladReloading.value = false;
  }
}

watch(selectedSbsSkladId, async () => {
  resetDraftMaterialChoicesBecauseSkladChanged();
  const sk = selectedSbsSkladId.value;
  if (sk != null && sk !== "") {
    await reloadWbsAfterSkladSelection();
  }
  scheduleRefreshBalances();
});

watch(filteredWbsRows, () => {
  scheduleRefreshBalances();
});

/** Черновики в плоской таблице (для «выбрать все»). */
function selectableFlatDraftMaterials() {
  const out = [];
  for (const mg of flatMaterialGroups.value) {
    for (const trow of getMaterialTableRows(mg)) {
      if (trow.kind === "draft" && !trow.mat.floor_map_document_id) {
        out.push(trow.mat);
      }
    }
  }
  return out;
}

const isFlatAllSelected = computed(() => {
  const s = selectableFlatDraftMaterials();
  return s.length > 0 && s.every((m) => m.selected);
});

const isFlatSomeSelected = computed(() => {
  const s = selectableFlatDraftMaterials();
  return s.some((m) => m.selected) && !isFlatAllSelected.value;
});

function toggleFlatSelectAll(selectAll) {
  const v = !!selectAll;
  for (const m of selectableFlatDraftMaterials()) {
    m.selected = v;
    if (v) {
      applyBuhQtyToFact(m);
    } else {
      m.fact_qty = 0;
    }
  }
}

const hasMaterials = computed(() => wbsData.value.length > 0);

function roundStable(x) {
  return Math.round(x * 1e8) / 1e8;
}

/** Начальное «кол-во к списанию»: только 0 или значение с бэка; авторасчёт от сметы не подставляем. */
function initialFactQty(item) {
  if (item.fact_qty != null && item.fact_qty !== "") {
    const n = Number(item.fact_qty);
    return Number.isNaN(n) ? 0 : n;
  }
  return 0;
}

/** Одна строка на материал с бэка; при наличии уже созданных М19 — черновик для следующего документа. */
function normalizeWbsRows(items) {
  const groups = new Map();
  for (const item of items) {
    const wbsKey = wbsGroupKey(item);
    if (!groups.has(wbsKey)) groups.set(wbsKey, []);
    groups.get(wbsKey).push(item);
  }

  const out = [];
  for (const [wbsKey, rows] of groups) {
    const baseRaw = rows[0];
    /** Бэкенд не отдаёт список документов; достаточно флагов строки и суммы М19 по материалу. */
    const hasSavedM19 =
      rows.some((r) => r.floor_map_document_id != null || r.material_overuse_id != null) ||
      (Number(baseRaw.total_m19_fact_qty) || 0) > 0;

    const base = {
      ...baseRaw,
      fact_qty: initialFactQty(baseRaw),
      comments: baseRaw.comments || "",
      selected: false,
      _key: `${wbsKey}_row`,
      isDraftRow: false,
      m19_remainder_exhausted: isM19RemainderExhausted({
        material_amount: baseRaw.material_amount,
        total_m19_fact_qty: baseRaw.total_m19_fact_qty,
      }),
    };
    if (base.m19_remainder_exhausted) {
      base.selected = false;
    }
    out.push(base);

    if (hasSavedM19 && !base.m19_remainder_exhausted) {
      out.push({
        ...base,
        material_overuse_id: null,
        floor_map_document_id: null,
        doc_is_avr_sent_bi: false,
        doc_is_file_sent_bi: false,
        fact_qty: 0,
        comments: "",
        selected: false,
        _key: `${wbsKey}_draft`,
        isDraftRow: true,
      });
    }
  }
  return out;
}

async function getData() {
  loading.value = true;
  const projectId = props.data?.project_id;
  const residentId = props.data?.params?.resident_id;
  const projectTypeId =
    props.data?.params?.project_type_id ?? props.data?.filters?.project_type_id ?? null;
  if (residentId == null || projectTypeId == null || projectTypeId === "") {
    loading.value = false;
    showErrorSnackbar("Не заданы ЖК (resident_id) или тип проекта для загрузки материалов М19.");
    return;
  }
  if (!projectId) {
    loading.value = false;
    showErrorSnackbar("Не указан проект для создания документа М19.");
    return;
  }
  const res = await getM19OverWbs(residentId, projectTypeId);
  loading.value = false;
  if (!res) return;

  const items = res.data || [];
  wbsData.value = normalizeWbsRows(items);
  await loadSkladOptions();
  scheduleRefreshBalances();
}

function onMaterialSelect(mat) {
  if (mat.selected) {
    applyBuhQtyToFact(mat);
  } else {
    mat.fact_qty = 0;
  }
}

function formatQty(v) {
  if (v == null || v === "") return "—";
  const n = Number(v);
  if (Number.isNaN(n)) return "—";
  return formatQtyNum(n);
}

/** Столбец «Резерв»: М‑19 в ожидании отправки файла в BI (`is_file_sent_bi`; по складу из запроса WBS). */
function formatReserveCell(row) {
  if (row == null) return "—";
  const v = row.m19_reserve_pending_avr_qty;
  if (v === null || v === undefined || v === "") {
    return "—";
  }
  const n = Number(v);
  if (Number.isNaN(n)) return "—";
  return formatQtyNum(n);
}

/**
 * Сумма факта по позиции: уже в БД (total_m19_fact_qty) + введённое по выбранным черновикам.
 * Как в material_overuse__create_document: existing_fact + batch ≤ smeta.
 */
function getTotalM19WithDrafts(mat) {
  let total = Number(mat.total_m19_fact_qty) || 0;
  const key = wbsGroupKey(mat);
  for (const r of wbsData.value) {
    if (wbsGroupKey(r) !== key) continue;
    if (r.floor_map_document_id) continue;
    if (r.selected) total += Number(r.fact_qty) || 0;
  }
  return roundStable(total);
}

/** Подсветка: итого по позиции (БД + черновик) больше кол-ва по смете. */
function exceedsSmetaLimit(mat) {
  const smeta = roundStable(Number(mat.material_amount) || 0);
  const total = getTotalM19WithDrafts(mat);
  return total > smeta + 1e-9;
}

function formatQtyNum(x) {
  if (x == null || Number.isNaN(Number(x))) return "—";
  const r = roundStable(Number(x));
  return String(r.toFixed(8)).replace(/\.?0+$/, "");
}

function buildMaterialsPayload() {
  return wbsData.value
    .filter((item) => item.selected)
    .map((item) => ({
      floor_map_id: item.floor_map_id,
      work_set_id: item.work_set_id,
      material_id: item.material_id,
      work_set_check_group_id: item.work_set_check_group_id,
      smeta_qty: item.material_amount,
      fact_qty: item.fact_qty,
      comments: item.comments || null,
    }));
}

/** Как на бэке: сумма факта по позиции (уже в М19 + новый документ) не больше сметы. */
function validateWriteoffNotAboveSmetaForSelected() {
  const selected = wbsData.value.filter((item) => item.selected);
  const seen = new Set();
  for (const item of selected) {
    const key = wbsGroupKey(item);
    if (seen.has(key)) continue;
    seen.add(key);
    const smeta = Number(item.material_amount) || 0;
    const total = getTotalM19WithDrafts(item);
    if (roundStable(total) > roundStable(smeta)) {
      const name = item.material_name ? `${item.material_name}: ` : "";
      const saved = Number(item.total_m19_fact_qty) || 0;
      const inDraft = roundStable(total - saved);
      return (
        `${name}сумма факта по М19 (${formatQtyNum(total)}) не может превышать количество по смете (${formatQtyNum(smeta)}). ` +
        `Уже учтено: ${formatQtyNum(saved)}, в новом документе: ${formatQtyNum(inDraft)}.`
      );
    }
  }
  return null;
}

function validateWriteoffNotAboveBuhForSelected() {
  if (!isSkladSelected.value) return null;
  if (loading1cBalances.value) {
    return "Дождитесь загрузки остатков 1С.";
  }
  const midSet = new Set();
  for (const row of wbsData.value) {
    if (!row.selected || row.material_id == null) continue;
    midSet.add(row.material_id);
  }
  for (const mid of midSet) {
    const sum = sumSelectedFactForMaterial(mid);
    if (sum <= 1e-9) continue;
    const gross = qty1cNumeric(mid);
    const reserve = pendingReserveQtyUntilFileSentBi(mid);
    const cap = qty1cNetAvailableForWrites(mid);
    let label = "";
    for (const r of wbsData.value) {
      if (r.material_id !== mid || !r.selected) continue;
      if (r.material_name) label = `${r.material_name}: `;
      break;
    }
    if (gross == null) {
      return `${label}нет данных об остатке в бухучёте 1С для выбранного склада`;
    }
    if (roundStable(sum) > roundStable(cap)) {
      return (
        `${label}сумма «к списанию» по материалу (${formatQtyNum(sum)}) превышает свободный остаток по 1С на складе (${formatQtyNum(cap)}: «Кол-во в бух» ${formatQtyNum(gross)} − «Резерв» ${formatQtyNum(reserve)}).`
      );
    }
  }
  return null;
}

async function onDownloadPreview() {
  downloadingPreview.value = true;
  try {
    const projectId = props.data?.project_id;
    const materials = buildMaterialsPayload();
    const v = validateWriteoffNotAboveSmetaForSelected();
    if (v) {
      showErrorSnackbar(v);
      return;
    }
    const vbuh = validateWriteoffNotAboveBuhForSelected();
    if (vbuh) {
      showErrorSnackbar(vbuh);
      return;
    }
    await downloadM19OverPreview(projectId, materials);
    showSucceedSnackbar("Предпросмотр успешно скачан");
  } catch (e) {
    showErrorSnackbar(
      e?.response?.data?.message || e?.message || "Ошибка при скачивании предпросмотра"
    );
  } finally {
    downloadingPreview.value = false;
  }
}

async function onSave() {
  const materials = buildMaterialsPayload();
  if (materials.length === 0) {
    showErrorSnackbar("Выберите хотя бы один материал");
    return;
  }
  if (selectedSbsSkladId.value == null || selectedSbsSkladId.value === "") {
    showErrorSnackbar("Выберите склад для документа M19");
    return;
  }
  const v = validateWriteoffNotAboveSmetaForSelected();
  if (v) {
    showErrorSnackbar(v);
    return;
  }
  const vbuh = validateWriteoffNotAboveBuhForSelected();
  if (vbuh) {
    showErrorSnackbar(vbuh);
    return;
  }
  saving.value = true;
  const projectId = props.data?.project_id;
  const res = await createM19Over(projectId, materials, selectedSbsSkladId.value);
  saving.value = false;

  if (!res) return;

  showSucceedSnackbar("M19 Перерасход создан");
  closeModal();

  if (props.data?.onSuccess) {
    setTimeout(() => props.data.onSuccess(), 100);
  }
}

onBeforeUnmount(() => {
  clearTimeout(balancesRefreshTimer);
  clearTimeout(searchDebounceTimer);
});

onMounted(() => {
  getData();
});
</script>

<style scoped lang="scss">
/* ─── M19 Enterprise (Fiori / MUI Pro-подобная сетка, светлая) ─── */
.tabular-nums {
  font-variant-numeric: tabular-nums;
}

.m19-enterprise {
  box-sizing: border-box;
  width: 100%;
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
  flex-direction: column;
  padding: 2px 0 8px;
  overflow: hidden;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  text-rendering: optimizeLegibility;
  color: #1e293b;
}

.m19-enterprise__progress {
  margin-bottom: 8px;
}

.m19-enterprise__toolbar {
  flex-shrink: 0;
  display: flex;
  flex-wrap: wrap;
  gap: 8px 12px;
  align-items: center;
  justify-content: flex-start;
  padding: 8px 0 14px;
}

.m19-enterprise__toolbar-controls {
  display: flex;
  align-items: center;
  gap: 10px;
  flex: 0 0 auto;
}

.m19-enterprise__sklad-prompt {
  margin: 2px 0 10px;
  padding: 0;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  max-width: 100%;
  font-size: 15px;
  font-weight: 600;
  line-height: 1.35;
  color: #1565c0;
  border: none;
  background: none;
  box-shadow: none;
}

.m19-enterprise__sklad-prompt-icon {
  flex-shrink: 0;
  /* у Material Icons контур симметричен с «пустым» полем слева — компенсируем визуальный сдвиг */
  transform: translateX(-2px);
}

.m19-enterprise__search {
  flex: 0 0 auto;
  width: 300px;
  max-width: min(360px, 63vw);

  :deep(.q-field__control) {
    min-height: 36px;
    border-radius: 8px;
    background: #f8f9fb;
    border-color: rgba(0, 0, 0, 0.06);
    padding-right: 4px;
  }

  :deep(.q-field__prepend) {
    height: 36px;
    padding-left: 10px;
    color: #9ca3af;
  }

  :deep(.q-field__marginal) {
    height: 36px;
  }

  :deep(.q-field__label) {
    font-size: 12px;
  }
}

.m19-enterprise__sklad {
  flex: 0 0 auto;
  width: 372px;
  max-width: min(450px, 81vw);

  :deep(.q-field__control) {
    min-height: 36px;
    border-radius: 8px;
    padding-right: 4px;
  }

  :deep(.q-field__marginal) {
    height: 36px;
  }

  :deep(.q-field__label) {
    font-size: 12px;
  }
}

.m19-enterprise__hint {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 11px;
  font-weight: 500;
  color: #64748b;
  flex-shrink: 0;
}

.m19-enterprise__search :deep(.q-field__native),
.m19-enterprise__search :deep(input),
.m19-enterprise__sklad :deep(.q-field__native) {
  font-size: 13px;
  font-weight: 500;
  color: #1e293b;
}

.m19-enterprise__table-host {
  flex: 1 1 auto;
  min-height: 0;
  border: 1px solid #e9ecef;
  border-radius: 12px;
  overflow: hidden;
  background: #fff;
  box-shadow: 0 1px 3px rgba(15, 23, 42, 0.04);
}

.m19-enterprise__table-scroll {
  max-height: min(42vh, calc(72vh - 200px));
  overflow-x: hidden;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;

  &::-webkit-scrollbar {
    width: 6px;
    height: 0;
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(0, 0, 0, 0.1);
    border-radius: 999px;
  }
}

.m19-grid {
  width: 100%;
  border-collapse: collapse;
  border-spacing: 0;
  table-layout: fixed;
  font-size: 13px;
  line-height: 1.4;
}

.m19-grid__head {
  position: sticky;
  top: 0;
  z-index: 4;

  tr {
    border-bottom: 1px solid #e2e8f0;
  }
}

.m19-grid__th {
  padding: 10px 12px;
  text-align: left;
  font-weight: 700;
  font-size: 12px;
  letter-spacing: 0.01em;
  color: #334155;
  background: #f4f6f9;
  border-bottom: 1px solid #e8ecf0;
  border-right: 1px solid #e8ecf0;

  &:last-child {
    border-right: none;
  }
}

.m19-grid__th--chk {
  width: 42px;
}

.m19-grid__th--mat,
.m19-grid__td--mat {
  width: 34%;
  min-width: 16rem;
}

.m19-grid__th--mat {
  text-align: left;
  vertical-align: bottom;
}

.m19-th-mat-stack {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
  line-height: 1.28;
}

.m19-th-mat-stack__top,
.m19-th-mat-stack__bot {
  display: block;
  font-weight: 600;
  font-size: 11px;
  color: #334155;
}

.m19-th-mat-stack__bot {
  font-weight: 500;
  font-size: 10.5px;
  color: #64748b;
}

.m19-grid__th--smeta,
.m19-grid__td--smeta {
  width: 10.5rem;
  min-width: 9rem;
  max-width: none;
}

.m19-th-smeta-stack {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  line-height: 1.28;
}

.m19-th-smeta-stack__top,
.m19-th-smeta-stack__bot {
  display: block;
  font-weight: 600;
  font-size: 11px;
  color: #334155;
}

.m19-th-smeta-stack__top {
  color: #27642b;
}

.m19-th-smeta-stack__bot {
  color: #5f2f7a;
}

.m19-th-sklad-stack {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  line-height: 1.28;
}

.m19-th-sklad-stack__top,
.m19-th-sklad-stack__bot {
  display: block;
  font-weight: 600;
  font-size: 11px;
  color: #334155;
}

.m19-th-sklad-stack__bot {
  color: #64748b;
  font-weight: 500;
  font-size: 10.5px;
}

.m19-grid__th--sklad {
  text-align: center;
}

.m19-grid__th--buh {
  text-align: right;
}

.m19-grid__th--num,
.m19-grid__td--num {
  width: 6.5rem;
  min-width: 6rem;
  max-width: none;
  white-space: normal;
  overflow: visible;
  word-break: break-word;
  overflow-wrap: anywhere;
}

.m19-grid__th--reserve,
.m19-grid__td--reserve {
  width: 5.75rem;
  min-width: 5.25rem;
}

.m19-grid__th--buh,
.m19-grid__td--buh {
  width: 7rem;
  min-width: 6.25rem;
  max-width: none;
}

.m19-grid__th--qty,
.m19-grid__td--qty {
  width: 7rem;
  min-width: 0;
  max-width: 7rem;
}

.m19-grid__th--cmt,
.m19-grid__td--cmt {
  width: 26%;
  min-width: 11rem;
}

.m19-grid__th--sklad,
.m19-grid__td--sklad {
  width: 22%;
  min-width: 10rem;
}

.m19-grid__td {
  padding: 10px 12px;
  vertical-align: top;
  box-sizing: border-box;
  color: #1e293b;
  font-weight: 500;
  background: #ffffff !important;
  min-width: 0;
  border-bottom: 1px solid #eef1f5;
  border-right: 1px solid #f4f6f9;

  &:last-child {
    border-right: none;
  }
}

.m19-grid__row:hover .m19-grid__td {
  background: #fafbfd !important;
}

.m19-grid tbody tr:last-child .m19-grid__td {
  border-bottom: 1px solid #eef1f5;
}

.m19-grid__td--mat {
  vertical-align: top;
  padding-top: 8px;
  padding-bottom: 8px;
}

.m19-grid__td--smeta,
.m19-grid__td--buh,
.m19-grid__td--sklad {
  vertical-align: top;
}

.m19-grid__td--chk,
.m19-grid__td--qty,
.m19-grid__td--cmt {
  vertical-align: top;
}

.m19-mat {
  position: relative;
  min-width: 0;
  cursor: default;
  align-self: flex-start;
}

.m19-mat__touch {
  min-width: 0;
}

.m19-mat__title {
  font-size: 14px;
  font-weight: 400;
  color: #0a0f1a;
  line-height: 1.38;
  display: block;
  white-space: normal;
  overflow: visible;
  word-break: normal;
  overflow-wrap: break-word;
  hyphens: auto;
}

.m19-mat__unit-inline {
  margin-left: 0.04em;
  color: #1976d2;
  font-size: 0.93em;
  font-weight: 500;
}

.m19-so-stack {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 5px;
}

.m19-so-stack__line {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
  justify-content: flex-start;
  text-align: center;
}

.m19-so-stack__line--value-only {
  gap: 0;
}

.m19-so-stack__val {
  display: block;
  width: 100%;
  font-size: 12px;
  font-weight: 700;
  line-height: 1.35;
  white-space: normal;
  word-break: break-word;
  overflow-wrap: anywhere;
  font-variant-numeric: tabular-nums;
}

.m19-so-stack__val--smeta {
  color: #2e7d32;
}

.m19-so-stack__val--oper {
  color: #7b1fa2;
}

.m19-num {
  font-weight: 600;
  line-height: 1.35;
  font-variant-numeric: tabular-nums;
}

.m19-grid__td--num.m19-num {
  white-space: normal;
  word-break: break-word;
  overflow-wrap: anywhere;
}

.m19-num-tone--neg {
  color: #c45c5c;
  font-weight: 700;
}

.m19-num-tone--pos {
  color: #3d8a62;
  font-weight: 700;
}

.m19-num-tone--zero {
  color: #94a3b8;
}

.m19-num-tone--muted {
  color: #bdbdbd;
}

.m19-buh-value {
  display: inline-block;
  font-size: 12px;
  font-weight: 600;
  line-height: 1.35;
  color: #1e293b;
  white-space: normal;
  word-break: break-word;
  overflow-wrap: anywhere;
}

.m19-buh-value--muted {
  color: #94a3b8;
  font-weight: 500;
}

.m19-buh-value--warn {
  color: #b45309;
  font-weight: 600;
}

.m19-grid__td--buh {
  min-width: 0;
  overflow: visible;
  text-align: right;
}

.m19-inline-qty {
  display: flex;
  align-items: flex-start;
  gap: 2px;
  justify-content: flex-end;
  width: 100%;
  min-width: 0;
  max-width: 100%;
}

.m19-inline-qty__warn {
  flex-shrink: 0;
  margin-top: 8px;
}

.m19-input-qty {
  flex: 1 1 auto;
  min-width: 0;
  max-width: 100%;
  width: 0;

  :deep(.q-field__inner) {
    min-width: 0;
  }

  :deep(.q-field__control) {
    border-radius: 10px;
    min-height: 36px;
    width: 100%;
  }

  &.q-field--focused :deep(.q-field__control):before {
    border-width: 2px;
    border-color: #1976d2;
  }

  &--warn :deep(.q-field__control:before) {
    border-color: #ed6c02;
  }

  :deep(input) {
    font-size: 13px;
    font-weight: 500;
    color: #1e293b;

    &::-webkit-outer-spin-button,
    &::-webkit-inner-spin-button {
      -webkit-appearance: none;
      margin: 0;
    }
  }
}

.m19-input-comment {
  width: 100%;
  min-width: 0;

  :deep(.q-field__control) {
    border-radius: 10px;
    min-height: 36px;
  }

  :deep(.q-field__native) {
    font-size: 13px;
    font-weight: 500;
    color: #1e293b;
  }

  &.q-field--focused :deep(.q-field__outline) {
    color: #1976d2 !important;
  }

  &.q-field--focused :deep(.q-field__control):before {
    border-width: 2px !important;
  }
}

.m19-sklad-cell {
  position: relative;
  min-width: 0;
  cursor: default;
}

.m19-sklad-cell__touch {
  min-width: 0;
}

.m19-sklad-cell__title {
  font-size: 13px;
  font-weight: 600;
  color: #1e293b;
  line-height: 1.4;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  word-break: break-word;
}

.m19-sklad-cell__sub {
  margin-top: 4px;
  font-size: 11px;
  line-height: 1.35;
  color: #94a3b8;
  display: -webkit-box;
  -webkit-line-clamp: 1;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  word-break: break-word;
}

.m19-sklad-tooltip .q-tooltip {
  max-width: 420px;
}

.m19-enterprise__footer {
  flex-shrink: 0;
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 14px;
  padding: 16px 4px 0;
  margin-top: 14px;
  border-top: 1px solid #eceff2;
  position: sticky;
  bottom: 0;
  z-index: 5;
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.94), #fff);

  .customBtn {
    width: auto !important;
    min-width: 140px !important;
    height: 44px !important;
    border-radius: 10px;
    font-weight: 650;
  }

  .m19-enterprise__btn--primary.customBtn {
    box-shadow: 0 4px 14px rgba(25, 118, 210, 0.12);
  }
}

.m19-grid__td--cmt {
  vertical-align: top;
}

.m19-grid__row.m19-row--exhausted .m19-grid__td {
  filter: saturate(0.72);
}

.m19-grid__row.m19-row--exhausted .m19-mat__title {
  color: #64748b;
  font-weight: 400;
}

.m19-grid__row.row-selected .m19-mat__title {
  color: #0a0f1a;
  font-weight: 400;
}


.m19-grid__row.row-selected .m19-grid__td:first-child {
  box-shadow: inset 4px 0 0 #8aa3b8;
}

.m19-grid__row.row-selected:hover .m19-grid__td {
  background: #f6f8fa !important;
}

.m19-grid__row.m19-row--over-smeta.row-selected .m19-grid__td:first-child {
  box-shadow: inset 4px 0 0 #d4a574;
}

.m19-grid__row.m19-row--over-smeta.row-selected .m19-grid__td {
  border-bottom-color: #eee5dc;
}

.m19-grid__row.m19-row--over-buh.row-selected .m19-grid__td:first-child {
  box-shadow: inset 4px 0 0 #e67e73;
}

.m19-grid__row.m19-row--over-buh.row-selected .m19-grid__td {
  border-bottom-color: #f2e8e7;
}

.m19-grid__row.m19-row--sub .m19-grid__td {
  border-top: 1px dashed #e8ecf0;
}

.m19-grid__row.m19-row--draft.row-unselected .m19-grid__td {
  background: #ffffff !important;
}

.cursor-help {
  cursor: help;
}
</style>
